import { TreeView } from '@fluid-experimental/tree';
import { logVerbose } from '../../../log';
import { matchCollectionChanged } from './deltaCollectionChanged';
import { matchItemsCleared, matchItemsChanged } from './deltaItemsChanged';
import { matchPropertyChanged } from './deltaPropertyChanged';
import { matchRelationshipChanged } from './deltaRelationshipChanged';

import { getChangeCategory, isEmptyDelta, matchCategoryFailed } from './deltaUtility';
import { Match, MatchFn, MfsChange, MfsChangeCategory } from './types';

const matchers: Record<MfsChangeCategory, MatchFn> = {
  [MfsChangeCategory.Unknown]: matchCategoryFailed,
  [MfsChangeCategory.Items]: matchItemsChanged,
  [MfsChangeCategory.Collection]: matchCollectionChanged,
  [MfsChangeCategory.Property]: matchPropertyChanged,
  [MfsChangeCategory.Relationship]: matchRelationshipChanged,
};

/** Gets the delta {@link MfsChange} between the `view` and `previousView`. */
export function getMfsChangeDelta(view: TreeView, previousView: TreeView): Match<MfsChange[]> {
  // processing diff is very inefficient for clear items, so we just check for it first.
  logVerbose('getMfsChangeDelta', 'views root', { viewRoot: view.root, previousViewRoot: previousView.root });
  const result = matchItemsCleared(view, previousView);
  if (result.isOK === true) {
    logVerbose('getMfsChangeDelta', 'root items cleared');
    return result;
  }

  const diff = previousView.delta(view);
  if (isEmptyDelta(diff)) {
    // tree raises change events without any actual difference (e.g collection items getting removed again). This should be a no-op.
    logVerbose('getMfsChangeDelta', `No actual diff, ignore changes`);
    // just return empty changes with success, so that it's ignored without error.
    return Match.ok([]);
  }
  const { delta, category } = getChangeCategory(view, previousView, diff);
  logVerbose('getMfsChangeDelta', 'change category', { category });
  return matchers[category](view, previousView, delta);
}
